import { AxisLabelsFormatterContextObject, Options, YAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighStock from 'highcharts/highstock';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import { Currency, DemeterChartSeriesStyle, DemeterDataFrequency, DemeterFilterTimeSpan, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import { ChartBuilderRequest } from '../../../Pages/ChartBuilder/ChartBuilderDefinitions';
import formattingService from '../../../Services/Formatting/FormattingService';
import { EventActionsEnum, EventCategoriesEnum, EventDataTargetsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import {
    chartColors,
    ChartContext,
    ChartDisplayType,
    ChartOptionsDefinitions,
    defalutLineWidth,
    defaultChartAxisTickLabelStyle,
    defaultChartAxisTitleStyle,
    defaultChartOptions,
    defaultZoneAxis,
    dotDashStyle,
    featuredLineWidth,
    HighchartsPlot,
    IChartPriceDataSeries,
    IChartProps,
    lineChartType,
    longDashStyle,
    shortDashStyle,
    solidStyle,
} from '../ChartDefinitions';
import chartService from '../ChartService';

export interface ICustomChartRawProps extends IChartProps {
    chartBuilderRequest?: ChartBuilderRequest;
    displayType?: ChartDisplayType;
    linesSeries: IChartPriceDataSeries[]; // TODO: this will be updated.
    dataFrequency: DemeterDataFrequency;
    currencies: { y1: Currency; y2: Currency };
    unitOfMeasures: { y1: UnitOfMeasure; y2: UnitOfMeasure };
    filterTimeSpan?: DemeterFilterTimeSpan;
    displayDecimalPlacesMinimum?: number;
    displayDecimalPlacesMaximum?: number;
    visibleIndexes?: number[];
}

type YAxisOptionExtended = YAxisOptions & { currency?: Currency; unitOfMeasure?: UnitOfMeasure };

const lineSeriesBufferPercent = 5;
const defaultTickAmount = 6;
const seriesIdPrefix = 'customChartRaw';

const CustomChartRaw: React.FC<ICustomChartRawProps> = (props: ICustomChartRawProps) => {
    // Chart hooks.
    const chartReference = useRef<HighchartsReact.RefObject>();
    const [forecastDataOn, setForecastDataOn] = useState<boolean>(true);

    const [highchartOptions, setHighchartOptions] = useState<Options>(() => ({
        ...defaultChartOptions,
        xAxis: {
            ...defaultChartOptions.xAxis,
            crosshair: {
                snap: true,
                dashStyle: longDashStyle,
            },
        },
        yAxis: [],
        series: [],
        navigator: {
            ...defaultChartOptions.navigator,
            enabled: !!props.chartBuilderRequest?.showTimespanControls,
            maskFill: scssVariables.ruleSetTwoAreaColor1,
            series: {
                dataGrouping: {
                    enabled: false,
                },
            },
        },
    }));

    const getDataSeriesDefinitions = useCallback(() => {
        const linesDataSeries = props.linesSeries.flatMap((lineSeries, index) => {
            const color = lineSeries.color ?? chartColors.lineChartColorsRuleSetOne[index % chartColors.lineChartColorsRuleSetOne.length];
            const primaryLine = (index === 0 && lineSeries.isPrimaryLine !== false) || lineSeries.isPrimaryLine;
            return [
                {
                    ...chartService.getDataSeriesBase(lineSeries, lineChartType, lineSeries.color ?? color),
                    id: `${seriesIdPrefix}${index}`,
                    yAxis: lineSeries.yAxis ?? 0,
                    events: {
                        legendItemClick() {
                            handleLegendItemClicked(this as unknown as ChartContext);
                        },
                    },
                    zoneAxis: defaultZoneAxis,
                    zones: [
                        {
                            value: 0,
                        },
                        {
                            dashStyle: lineSeries.style === DemeterChartSeriesStyle.Bar ? solidStyle : longDashStyle,
                            borderColor: color,
                            color: lineSeries.style === DemeterChartSeriesStyle.Bar ? scssVariables.plainWhite : color,
                        },
                    ],
                    showInNavigator: true,
                    lineWidth: primaryLine ? featuredLineWidth : defalutLineWidth,
                    turboThreshold: 0,
                    visible: props.visibleIndexes ? props.visibleIndexes.includes(index) : true,
                },
                // These forecast x-axis registrations are there just for the legend.
                chartService.getForecastDataSeriesBase(lineSeries, lineSeries.color ?? color, handleForecastClicked),
                {
                    name: lineSeries.forwardCurveLabel,
                    yAxis: lineSeries.yAxis ?? 0,
                    marker: {
                        enabled: false,
                    },
                    events: {
                        legendItemClick() {
                            handleLegendItemClicked(this as unknown as ChartContext);
                        },
                    },
                    data: [] as HighchartsPlot[],
                    zoneAxis: defaultZoneAxis,
                    dashStyle: lineSeries.style === DemeterChartSeriesStyle.Bar ? solidStyle : dotDashStyle,
                    showInLegend: true,
                    showInNavigator: true,
                    visible: props.visibleIndexes ? props.visibleIndexes.includes(index) : true,
                },
            ];
        });

        return linesDataSeries;
    }, [props.linesSeries, forecastDataOn]);

    const getYAxisOptions = useCallback(() => {
        const initialYAxisTitle = {
            title: {
                text: '',
                style: defaultChartAxisTitleStyle,
            },
        };
        const initialYAxisLabel = {
            labels: {
                format: '{value:{point.y: , .0f}',
                style: defaultChartAxisTickLabelStyle,
            },
        };
        const initialYAxis: YAxisOptionExtended = {
            ...initialYAxisTitle,
            ...initialYAxisLabel,
            tickAmount: defaultTickAmount,
            crosshair: {
                snap: true,
                dashStyle: shortDashStyle,
            },
        };

        let yAxisOptions: YAxisOptionExtended[] = [initialYAxis];

        if (props.linesSeries.some((x) => x.yAxis === 0)) {
            yAxisOptions[0].currency = props.currencies.y1;
            yAxisOptions[0].unitOfMeasure = props.unitOfMeasures.y1;
        }

        if (props.linesSeries.some((x) => x.yAxis)) {
            yAxisOptions.push({
                ...initialYAxis,
                title: {
                    text: '',
                    style: defaultChartAxisTitleStyle,
                },
                currency: props.currencies.y2,
                unitOfMeasure: props.unitOfMeasures.y2,
                opposite: true,
            });
        }

        if (!props.linesSeries.some((x) => x.currency || x.unitOfMeasure)) {
            return yAxisOptions;
        }

        const currentAndUnitOfMeasuresMap: { [key: string]: { currency?: Currency; unitOfMeasure?: UnitOfMeasure; order: number } } = {};
        let order = 0;
        props.linesSeries.forEach((x) => {
            order += 1;

            currentAndUnitOfMeasuresMap[`${x.currency}/${x.unitOfMeasure}`] = {
                currency: x.currency,
                unitOfMeasure: x.unitOfMeasure,
                order,
            };
        });

        yAxisOptions = Object.values(currentAndUnitOfMeasuresMap)
            .sort((x) => x.order)
            .map((x, index) => ({
                ...yAxisOptions[0],
                title: { ...yAxisOptions[0].title },
                labels: { ...yAxisOptions[0].labels },
                crosshair: { ...(yAxisOptions[0].crosshair as HighStock.AxisCrosshairOptions) },
                currency: x.currency,
                unitOfMeasure: x.unitOfMeasure,
                opposite: index === 1,
            }));

        return yAxisOptions;
    }, [props.linesSeries]);

    // Main useEffect to update chart when props or data changes.
    useEffect(() => {
        const dataSeries = getDataSeriesDefinitions();
        const yAxisOptions = getYAxisOptions();
        const newYAxis = [...yAxisOptions];

        const today = new Date();
        const maximumValues = Array(newYAxis.length).fill(0);

        const downloadData = chartService.getDownloadData(props.linesSeries, props.dataFrequency);

        [...props.linesSeries].forEach((series, index) => {
            const lastActualValueDataIndex = series.data.findLastIndex((x) => x.isActualValue);
            const hasOnlyForecastData = series.data.length > 0 && lastActualValueDataIndex === -1;
            const currentDataSeriesIndex = index * 3;
            let firstForecastDate = today;

            if (series.data.length > 0 && hasOnlyForecastData) {
                firstForecastDate = series.data[0] && series.data[0].asOfDate;
            } else if (series.data.length > lastActualValueDataIndex + 1) {
                firstForecastDate = series.data[lastActualValueDataIndex]?.asOfDate;
            }
            const endIndex = forecastDataOn ? series.data.length : lastActualValueDataIndex + 1;
            const currentDataSeriesItem = dataSeries[currentDataSeriesIndex] as ChartOptionsDefinitions;
            currentDataSeriesItem.type = (series.style ?? ChartDisplayType.Line).toLowerCase();
            currentDataSeriesItem.data = series.data
                .slice(0, endIndex)
                .map((item) => ({ x: item?.asOfDate.getTime(), y: item.value, isActualValue: item.isActualValue }));

            if (series.forwardCurveData && series.forwardCurveData.length > 0) {
                firstForecastDate = series.forwardCurveData[0]?.asOfDate;
                (dataSeries[currentDataSeriesIndex + 2] as ChartOptionsDefinitions).data = series.forwardCurveData?.map((item) => ({
                    x: item?.asOfDate.getTime(),
                    y: item.value,
                    isActualValue: item.isActualValue,
                }));

                if (series.forwardCurveLineStyle) {
                    dataSeries[currentDataSeriesIndex + 2].dashStyle = series.forwardCurveLineStyle;
                }
            }

            newYAxis[series.yAxis ?? 0].maxPadding = lineSeriesBufferPercent / 100.0;
            newYAxis[series.yAxis ?? 0].minPadding = lineSeriesBufferPercent / 100.0;

            maximumValues[series.yAxis ?? 0] = Math.max(maximumValues[series.yAxis ?? 0], ...currentDataSeriesItem.data!.map((x) => Math.abs(x.y ?? 0)));

            // Push the point forward so it doesn't think the last actual is a forecast.
            // TODO: Do different math based on monthly/weekly data.
            currentDataSeriesItem.zones![0].value = (firstForecastDate?.getTime() ?? 0) + 1000;
            currentDataSeriesItem.dashStyle = hasOnlyForecastData ? longDashStyle : undefined;
            currentDataSeriesItem.showInLegend = series.data.length > 0;
            dataSeries[currentDataSeriesIndex + 1].showInLegend = series.data.length > 0 && !hasOnlyForecastData && firstForecastDate !== today;
            dataSeries[currentDataSeriesIndex + 2].showInLegend = (dataSeries[currentDataSeriesIndex + 2] as ChartOptionsDefinitions).data!.length > 0;
        });

        const newOptions = {
            ...highchartOptions,
            ...{ series: dataSeries },
            ...{ yAxis: newYAxis },
            ...{
                navigator: {
                    ...highchartOptions.navigator,
                    ...{
                        enabled: props.chartBuilderRequest?.showTimespanControls,
                    },
                },
            },
            ...{
                tooltip: {
                    formatter() {
                        const context = this as unknown as ChartContext;

                        return chartService.getTooltipText(context, {
                            nameOverride: !props.linesSeries.some((lineSeries) => lineSeries.forwardCurveLabel === newOptions.series[context.series.index].name)
                                ? newOptions.series![context.series.index + 1]?.name
                                : '',
                            dataFrequency: props.dataFrequency,
                            displayDecimalPlacesMinimum: props.displayDecimalPlacesMinimum ?? 0,
                            displayDecimalPlacesMaximum: props.displayDecimalPlacesMaximum ?? 4,
                        });
                    },
                },
            },
            downloadData,
        };

        newOptions.yAxis.forEach((axis, index) => {
            axis.labels!.formatter = function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                const context = this as unknown as AxisLabelsFormatterContextObject;
                return formattingService.formatNumber(context.value, {
                    maximumDecimalPlaces: formattingService.getDisplayDecimalPlacesMinimumForCharts(maximumValues[index] ?? 0),
                });
            };
            const currencyAndUnitOfMeasureText = chartService.getCurrencyAndUnitOfMeasureText(axis.unitOfMeasure, axis.currency) ?? '';
            const useLabels = Array.isArray(props.chartBuilderRequest?.labels) && props.chartBuilderRequest?.labels;
            const isFirstOrSecond = index === 1 || index === 0;

            if (useLabels && isFirstOrSecond) {
                axis!.title!.text = props.chartBuilderRequest?.labels![index] || currencyAndUnitOfMeasureText;
            } else {
                axis!.title!.text = currencyAndUnitOfMeasureText;
            }
        });

        setHighchartOptions(newOptions as Options);
        handleFilterTimeSpanChange();
    }, [forecastDataOn, props.displayType, props.linesSeries, getDataSeriesDefinitions]);

    useEffect(() => {
        handleFilterTimeSpanChange();
    }, [props.filterTimeSpan]);

    // Unique custom chart functions.
    const handleFilterTimeSpanChange = () => {
        if (!props.filterTimeSpan || props.linesSeries.length === 0 || props.linesSeries[0].data.length === 0) {
            return;
        }

        const oldestDate = Math.min(...props.linesSeries.map((x) => x.data[0]?.asOfDate.getTime()));
        const latestDataDate = Math.max(...props.linesSeries.map((x) => x.data[x.data.length - 1]?.asOfDate.getTime()));

        const latestForwardCurveDate = Math.max(
            ...props.linesSeries.map((x) => {
                if (x.forwardCurveData && x.forwardCurveData.length > 0) {
                    return x.forwardCurveData![x.forwardCurveData!.length - 1].asOfDate.getTime();
                }

                return 0;
            }),
        );
        chartReference.current?.chart.xAxis[0].setExtremes(oldestDate, Math.max(latestDataDate, latestForwardCurveDate));
    };

    const handleLegendItemClicked = (context: ChartContext) => {
        const seriesClicked = context.chart.series[context.index];

        loggingService.trackEventWithAnalytics(
            EventActionsEnum.ChartInteraction,
            EventCategoriesEnum.LegendItemClicked,
            seriesClicked.name,
            EventDataTargetsEnum.CustomGraph,
        );

        // If we clicked on a line series, also make the forward curve go away.
        if (context.index % 3 === 0) {
            if (seriesClicked.visible) {
                context.chart.series[context.index + 1].hide();
                context.chart.series[context.index + 2].hide();
            } else {
                if (forecastDataOn) {
                    context.chart.series[context.index + 1].show();
                }
                context.chart.series[context.index + 2].show();
            }
        } else if (!seriesClicked.visible && !context.chart.series[context.index - 2].visible) {
            // If we click on the forward curve while the lines series is gone, bring it back as well.
            context.chart.series[context.index - 2].show();
            if (forecastDataOn) {
                context.chart.series[context.index - 1].show();
            }
        }
    };

    const handleForecastClicked = (context: ChartContext) => {
        const seriesClicked = context.chart.series[context.index];

        loggingService.trackEventWithAnalytics(
            EventActionsEnum.ChartInteraction,
            EventCategoriesEnum.LegendItemClicked,
            seriesClicked.name,
            EventDataTargetsEnum.CustomGraph,
        );
        context.chart.series.forEach((item, index) => {
            if (index % 3 !== 1 || index === context.index) {
                return;
            }

            if (forecastDataOn) {
                item.hide();
            } else {
                item.show();
            }
        });

        setForecastDataOn(!forecastDataOn);
    };

    return (
        <HighchartsReact
            ref={(newChartReference: HighchartsReact.RefObject) => {
                if (chartReference.current || !newChartReference) {
                    return;
                }

                chartReference.current = newChartReference;
                if (props.chartReference) {
                    props.chartReference(newChartReference);
                }
            }}
            highcharts={HighStock}
            options={highchartOptions}
            containerProps={{ style: { height: '100%' } }}
        />
    );
};

export default memo(CustomChartRaw);
